import React, { Component } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PubNub from 'pubnub';
import { FiArrowLeft, FiUser, FiPlus, FiRefreshCw } from 'react-icons/fi';
import {
  loadOpsData, setAgentType, toggleLive, triggerManualRedial,
} from '../../actions/autoDialOps';
import config from '../../config';
import visitLogo from '../../images/common/visit-logo.svg';

import {
  C, Page, TopNav, BackBtn, VisitLogo, LogoDivider, NavTitle, NavUser,
  ContentHeader, PageTitle, HeaderActions, HeaderBtn,
  LiveBadge, LiveDot, Segmented, SegmentedItem,
} from './chrome';

import KpiStrip         from './components/KpiStrip';
import RightNowPanel    from './components/RightNowPanel';
import TodayPanel       from './components/TodayPanel';
import DeepDiveTabs     from './components/DeepDiveTabs';
import ManualRedialModal from './components/ManualRedialModal';
import AgentRosterPanel from './components/AgentRosterPanel';

// PubNub pushes near-instant deltas (auto_dial_started, auto_dial_ended,
// manual_redial_queued, agent_role_changed). The interval poll is now a
// safety net for missed events (network blip, server restart) — long enough
// to be cheap, short enough that staleness never feels broken.
const POLL_INTERVAL_MS = 60 * 1000;
const REFRESH_DEBOUNCE_MS = 500;

// Insurer → PubNub channel. Mirrors utils/auto-dial-ops-pubnub.js on the
// server. Each ops user only subscribes to their insurer's channel.
const OPS_CHANNEL_BY_INSURER = {
  max:        'auto-dial-ops-max',
  starhealth: 'auto-dial-ops-starhealth',
};

const INSURER_LABEL = {
  max:        'MAX · Auto-Dial Ops',
  starhealth: 'Starhealth · Auto-Dial Ops',
};

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.6rem;
  margin-bottom: 2.4rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const ErrorBar = styled.div`
  background: ${C.redDim};
  border: 1px solid ${C.red}40;
  color: ${C.red};
  border-radius: 0.6rem;
  padding: 1rem 1.4rem;
  font-size: 1.2rem;
  margin-bottom: 1.6rem;
`;

const LastRefreshed = styled.div`
  font-size: 1.1rem;
  color: ${C.textDim};
  font-variant-numeric: tabular-nums;
`;

class AutoDialOpsCenter extends Component {
  state = {
    redialModalOpen: false,
    prefillApplicationNo: null,
  };

  componentDidMount() {
    this.props.loadOpsData();
    this.startPolling();
    this.setupPubNub();
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  componentDidUpdate(prev) {
    if (prev.live !== this.props.live) {
      if (this.props.live) this.startPolling();
      else this.stopPolling();
    }
  }

  componentWillUnmount() {
    this.stopPolling();
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    if (this.pubnub) {
      try { this.pubnub.unsubscribeAll(); } catch (e) { /* noop */ }
      this.pubnub = null;
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  startPolling = () => {
    this.stopPolling();
    if (!this.props.live) return;
    if (document.hidden) return;
    this.pollTimer = setInterval(() => this.props.loadOpsData(), POLL_INTERVAL_MS);
  };

  stopPolling = () => {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  };

  // ─── Real-time push (PubNub) ─────────────────────────────────────────────
  setupPubNub = () => {
    if (!config.pubnubPublishKey || !config.pubnubSubscribeKey) return;
    const channel = OPS_CHANNEL_BY_INSURER[this.props.userInsurer];
    if (!channel) return;
    try {
      this.pubnub = new PubNub({
        publishKey:   config.pubnubPublishKey,
        subscribeKey: config.pubnubSubscribeKey,
        uuid:         `auto-dial-ops-${Date.now()}`,
      });
      this.pubnub.addListener({
        message: ({ message }) => {
          if (!message || !message.type) return;
          this.scheduleRefresh();
        },
      });
      this.pubnub.subscribe({ channels: [channel] });
    } catch (e) {
      // PubNub init failures should never break the dashboard — the safety-net
      // poll keeps the data fresh.
    }
  };

  // Debounce: when several events fire in quick succession (e.g. the queue
  // processor finishes a 5-case batch), wait for the burst to settle, then
  // refresh exactly once.
  scheduleRefresh = () => {
    if (!this.props.live) return;
    if (document.hidden) return;
    if (this.refreshTimer) clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => {
      this.refreshTimer = null;
      this.props.loadOpsData();
    }, REFRESH_DEBOUNCE_MS);
  };

  // Pause poll when tab is hidden — saves API calls when ops alt-tabs away.
  // PubNub stays subscribed (cheap, no impact). On return we immediately
  // refresh in case events were dropped while hidden.
  onVisibilityChange = () => {
    if (document.hidden) {
      this.stopPolling();
    } else {
      this.startPolling();
      if (this.props.live) this.props.loadOpsData();
    }
  };

  openRedialModal = (applicationNo = null) =>
    this.setState({ redialModalOpen: true, prefillApplicationNo: applicationNo });

  closeRedialModal = () =>
    this.setState({ redialModalOpen: false, prefillApplicationNo: null });

  submitRedial = (apps, agentType) =>
    this.props.triggerManualRedial(apps, agentType);

  render() {
    const {
      agentType, live, loading, error, lastUpdatedAt,
      kpis, poolUtilization, hourlyTrend, failureBreakdown, oldestPending,
      pending, inFlight, exhausted, connected, agentPerformance,
      doctorFirstName, doctorLastName, history, userInsurer,
    } = this.props;
    const navLabel = INSURER_LABEL[userInsurer] || 'Auto-Dial Ops';

    const { redialModalOpen, prefillApplicationNo } = this.state;

    return (
      <Page>
        <TopNav>
          <BackBtn onClick={() => history.push('/home')}>
            <FiArrowLeft size={15} /> Back
          </BackBtn>
          <VisitLogo src={visitLogo} alt="Visit Health" />
          <LogoDivider />
          <NavTitle>{navLabel}</NavTitle>
          {(doctorFirstName || doctorLastName) && (
            <NavUser>
              <FiUser size={13} />
              <span>{doctorFirstName} {doctorLastName}</span>
            </NavUser>
          )}
        </TopNav>

        <ContentHeader>
          <TitleBlock>
            <PageTitle>Auto-Dial <span>Ops Center</span></PageTitle>
          </TitleBlock>

          <HeaderActions>
            <LastRefreshed>
              {lastUpdatedAt
                ? `Updated ${new Date(lastUpdatedAt).toLocaleTimeString()}`
                : 'Loading…'}
            </LastRefreshed>
            <HeaderBtn onClick={() => this.props.loadOpsData()} disabled={loading}>
              <FiRefreshCw size={14} /> Refresh
            </HeaderBtn>
            <HeaderBtn onClick={() => this.openRedialModal()}>
              <FiPlus size={14} /> Manual re-dial
            </HeaderBtn>
            <LiveBadge on={live} onClick={this.props.toggleLive} title="Toggle auto-refresh">
              <LiveDot color={live ? C.green : C.textMuted} pulse={live} />
              {live ? 'LIVE' : 'PAUSED'}
            </LiveBadge>
          </HeaderActions>
        </ContentHeader>

        <ToggleRow>
          <Segmented role="tablist" aria-label="Dial pool">
            <SegmentedItem
              active={agentType === 'doctor'}
              onClick={() => this.props.setAgentType('doctor')}
            >
              Doctor pool
            </SegmentedItem>
            <SegmentedItem
              active={agentType === 'tele_caller'}
              onClick={() => this.props.setAgentType('tele_caller')}
            >
              Tele-caller pool
            </SegmentedItem>
          </Segmented>
        </ToggleRow>

        {error && <ErrorBar>Failed to load: {error}</ErrorBar>}

        <KpiStrip kpis={kpis} />

        <TwoCol>
          <RightNowPanel
            utilization={poolUtilization}
            oldestPending={oldestPending}
          />
          <TodayPanel
            hourlyTrend={hourlyTrend}
            failureBreakdown={failureBreakdown}
            connectedToday={kpis.connectedToday || 0}
          />
        </TwoCol>

        <AgentRosterPanel
          agentTypeFromPage={agentType}
          onPoolChange={this.props.setAgentType}
        />

        <DeepDiveTabs
          pending={pending}
          inFlight={inFlight}
          exhausted={exhausted}
          connected={connected}
          agentPerformance={agentPerformance}
          onRequeue={(applicationNo) => this.openRedialModal(applicationNo)}
        />

        <ManualRedialModal
          open={redialModalOpen}
          agentType={agentType}
          prefillApplicationNo={prefillApplicationNo}
          onClose={this.closeRedialModal}
          onSubmit={this.submitRedial}
        />
      </Page>
    );
  }
}

const mapStateToProps = (state) => ({
  ...state.autoDialOps,
  doctorFirstName: state.user.doctorFirstName,
  doctorLastName:  state.user.doctorLastName,
  userInsurer:     state.user.insurer,
});

const mapDispatchToProps = {
  loadOpsData,
  setAgentType,
  toggleLive,
  triggerManualRedial,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutoDialOpsCenter);
